import { apiClient } from '@kss/api';

/**
 * Gift-card lookup + redemption math for the bill, mirroring coupons.ts.
 *
 * Field names match the LIVE /sales/GiftCard schema: cardNumber,
 * currentBalance, initialBalance, cardStatus ('Active'), expiresAt, pin.
 *
 * NOTE: there is no backend redeem endpoint yet (no atomic balance
 * decrement), so applying a card here reduces the payable amount best-effort
 * but does NOT debit the card server-side — see BACKEND_GAPS. Treat as a
 * discount/tender on the bill until a /sales/GiftCard/redeem action lands.
 */

export interface GiftCardRow {
  id?: number | string;
  code?: string;
  cardNumber?: string;
  currentBalance?: number;
  initialBalance?: number;
  currency?: string;
  cardStatus?: string;
  expiresAt?: string;
  isActive?: boolean | number;
}

export interface GiftCardResult {
  ok: boolean;
  card?: GiftCardRow;
  /** Rupee amount the card covers on this bill. */
  redeem: number;
  message: string;
}

function listOf(d: unknown): GiftCardRow[] {
  const b = (d ?? {}) as Record<string, unknown>;
  return (Array.isArray(b) ? b
    : Array.isArray(b.items) ? b.items
    : Array.isArray(b.data) ? b.data
    : Array.isArray((b.data as Record<string, unknown>)?.items) ? (b.data as { items: unknown[] }).items
    : []) as GiftCardRow[];
}

/** Find a gift card by its printed number (case-insensitive). */
export async function lookupGiftCard(cardNumber: string): Promise<GiftCardRow | null> {
  const trimmed = cardNumber.trim().toLowerCase();
  if (!trimmed) return null;
  let rows: GiftCardRow[] = [];
  try { rows = listOf((await apiClient.get(`/sales/GiftCard?cardNumber=${encodeURIComponent(cardNumber.trim())}`)).data); } catch { /* full list */ }
  if (rows.length === 0) {
    try { rows = listOf((await apiClient.get('/sales/GiftCard')).data); } catch { /* none */ }
  }
  return rows.find((c) => String(c.cardNumber ?? '').trim().toLowerCase() === trimmed
    || String(c.code ?? '').trim().toLowerCase() === trimmed) ?? null;
}

/** Validate a gift card and compute how much it covers on an order. */
export function applyGiftCard(card: GiftCardRow | null, orderValue: number): GiftCardResult {
  if (!card) return { ok: false, redeem: 0, message: 'Gift card not found' };
  if (card.isActive === false || card.isActive === 0) return { ok: false, redeem: 0, message: 'Gift card is inactive' };
  if (card.cardStatus && !/active/i.test(String(card.cardStatus))) {
    return { ok: false, redeem: 0, message: `Gift card is ${card.cardStatus}` };
  }
  if (card.expiresAt) {
    const t = Date.parse(String(card.expiresAt));
    if (!Number.isNaN(t) && Date.now() > t) return { ok: false, redeem: 0, message: 'Gift card has expired' };
  }
  const bal = Number(card.currentBalance ?? 0) || 0;
  if (bal <= 0) return { ok: false, redeem: 0, message: 'Gift card has no balance' };
  const redeem = Math.round(Math.min(bal, Math.max(0, orderValue)) * 100) / 100;
  if (redeem <= 0) return { ok: false, redeem: 0, message: 'Nothing to redeem on this order' };
  return {
    ok: true, card, redeem,
    message: `Gift card ${card.cardNumber ?? card.code ?? ''} — ₹${redeem.toLocaleString('en-IN')} applied`,
  };
}
